import {
  FC,
  createContext,
  useContext,
  useState,
  ReactNode,
  useCallback,
  useEffect,
} from 'react';
import { useLocalStorage } from 'react-use';
import {
  POPOUT_COMMAND_EVENT,
  PopoutCommand,
  STORAGE_KEYS,
  type PopoutCommandType,
} from '../api/constants';

interface PopoutContextType {
  isPopoutOpen: boolean;
  isPopoutMinimized: boolean;
  openPopout: () => void;
  closePopout: () => void;
  minimizePopout: () => void;
  expandPopout: () => void;
  togglePopout: () => void;
}

const PopoutContext = createContext<PopoutContextType | undefined>(undefined);

const dispatchPopoutCommand = (command: PopoutCommandType) => {
  window.dispatchEvent(
    new CustomEvent<PopoutCommandType>(POPOUT_COMMAND_EVENT, {
      detail: command,
    }),
  );
};

export const PopoutProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const [popoutState, setPopoutState] = useLocalStorage(
    STORAGE_KEYS.POPOUT_STATE,
    {
      isOpen: false,
      isMinimized: false,
    },
  );

  const [isPopoutOpen, setIsPopoutOpen] = useState(
    popoutState?.isOpen || false,
  );
  const [isPopoutMinimized, setIsPopoutMinimized] = useState(
    popoutState?.isMinimized || false,
  );

  // Sync state changes to localStorage
  useEffect(() => {
    setPopoutState({
      isOpen: isPopoutOpen,
      isMinimized: isPopoutMinimized,
    });
  }, [isPopoutOpen, isPopoutMinimized, setPopoutState]);

  useEffect(() => {
    const onCommand = (event: Event) => {
      const command = (event as CustomEvent<PopoutCommandType>).detail;
      switch (command) {
        case PopoutCommand.OPEN:
          setIsPopoutOpen(true);
          setIsPopoutMinimized(false);
          break;
        case PopoutCommand.CLOSE:
          setIsPopoutOpen(false);
          setIsPopoutMinimized(false);
          break;
        case PopoutCommand.MINIMIZE:
          setIsPopoutOpen(true);
          setIsPopoutMinimized(true);
          break;
        case PopoutCommand.EXPAND:
          setIsPopoutOpen(true);
          setIsPopoutMinimized(false);
          break;
        default:
          break;
      }
    };

    window.addEventListener(POPOUT_COMMAND_EVENT, onCommand);
    return () => window.removeEventListener(POPOUT_COMMAND_EVENT, onCommand);
  }, []);

  const openPopout = useCallback(() => {
    dispatchPopoutCommand(PopoutCommand.OPEN);
  }, []);

  const closePopout = useCallback(() => {
    dispatchPopoutCommand(PopoutCommand.CLOSE);
  }, []);

  const minimizePopout = useCallback(() => {
    dispatchPopoutCommand(PopoutCommand.MINIMIZE);
  }, []);

  const expandPopout = useCallback(() => {
    dispatchPopoutCommand(PopoutCommand.EXPAND);
  }, []);

  const togglePopout = useCallback(() => {
    if (isPopoutOpen && !isPopoutMinimized) {
      minimizePopout();
    } else if (isPopoutOpen && isPopoutMinimized) {
      expandPopout();
    } else {
      openPopout();
    }
  }, [
    isPopoutOpen,
    isPopoutMinimized,
    openPopout,
    minimizePopout,
    expandPopout,
  ]);

  const value: PopoutContextType = {
    isPopoutOpen,
    isPopoutMinimized,
    openPopout,
    closePopout,
    minimizePopout,
    expandPopout,
    togglePopout,
  };

  return (
    <PopoutContext.Provider value={value}>{children}</PopoutContext.Provider>
  );
};

export const usePopout = () => {
  const context = useContext(PopoutContext);
  if (!context) {
    throw new Error('usePopout must be used within a PopoutProvider');
  }
  return context;
};
